// src/components/generic/ImageCropEditor/backgroundRemoval.ts
//
// In-browser background removal. Wraps @imgly/background-removal, which runs an
// ONNX segmentation model client-side (no upload, no server) and returns the
// subject cut out onto transparency. The library is code-split via dynamic
// import so it (and its ~MBs of wasm/model) only loads the first time a user
// actually removes a background.
//
// Ported from grassequal-landing's headshot tool, where this approach is proven.

let removeBgFn: ((input: Blob, opts?: unknown) => Promise<Blob>) | null = null;
let loading: Promise<void> | null = null;

/** Load the library once (idempotent). Safe to call to warm the cache. */
export async function loadBackgroundRemoval(): Promise<void> {
  if (removeBgFn) {
    return;
  }

  if (!loading) {
    loading = import("@imgly/background-removal")
      .then((mod) => {
        removeBgFn = mod.removeBackground as typeof removeBgFn;
      })
      .catch((err) => {
        loading = null;
        throw err;
      });
  }

  return loading;
}

export interface RemoveBackgroundOptions {
  /** 0–100 progress callback (best-effort; the model reports load + inference). */
  onProgress?: (percent: number, label: string) => void;
}

/**
 * Remove the background from an image blob. Returns a PNG blob with the subject
 * on a transparent background. Throws on failure (unsupported image, model load
 * error, etc.) — callers should surface a friendly message.
 */
export async function removeBackground(input: Blob, opts: RemoveBackgroundOptions = {}): Promise<Blob> {
  await loadBackgroundRemoval();

  if (!removeBgFn) {
    throw new Error("Background removal is unavailable.");
  }

  const result = await removeBgFn(input, {
    progress: (key: string, current: number, total: number) => {
      if (!opts.onProgress || !total) {
        return;
      }
      const percent = Math.round((current / total) * 100);
      // key looks like "fetch:/models/..." or "compute:inference" — keep a short label.
      const label = key.startsWith("fetch") ? "Loading model" : "Removing background";
      opts.onProgress(percent, label);
    },
  });

  return result;
}

/**
 * Composite a (usually transparent) image blob onto a solid background color and
 * return the flattened result. Used when the user picks a background color after
 * removal instead of keeping transparency. Returns a PNG blob (preserves any
 * remaining edge alpha antialiasing against the fill).
 */
export async function compositeOnColor(image: Blob, color: string): Promise<Blob> {
  const url = URL.createObjectURL(image);
  try {
    const img = await loadImage(url);
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas unavailable.");
    }

    ctx.fillStyle = color;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Encode failed."))), "image/png");
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image."));
    img.src = src;
  });
}
